import { useQuery } from "@tanstack/react-query"
import { useLocation } from "react-router"

import {
  getAccountFunctionSegments,
  getAccountOfferings,
  getAccounts,
  getCollaborators,
  getLast5EmailTemplates,
  getOrbitshiftLocations,
  getSavedTemplate,
  getTenantUsersBasicDetails
} from "~/api/accounts"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"

const DEFAULT_PAYLOAD = {
  searchText: "",
  industries: ["all"],
  locations: ["all"],
  revenue: ["all"]
}
const fetchAccounts = async (
  tokenInfo: ITokenInfo,
  payload: any,
  linkedTenantId?: number
) => {
  const res = await getAccounts(tokenInfo, payload, linkedTenantId)
  return res.data
}
export function useGetAccounts(
  input?: {
    searchText?: string
    industries?: string[]
    locations?: string[]
    revenue?: string[]
  },
  enabled = false
) {
  const location = useLocation()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const linkedTenantId = useGlobalStore((store) => store.linkedTenantId)
  const payload = input ?? DEFAULT_PAYLOAD
  const stored =
    typeof window !== "undefined"
      ? sessionStorage.getItem("latest_accounts_key")
      : null
  const restoredPayload = stored
    ? input && JSON.stringify(input) !== stored
      ? input
      : JSON.parse(stored)
    : payload

  if (typeof window !== "undefined" && input) {
    sessionStorage.setItem("latest_accounts_key", JSON.stringify(payload))
  }
  const queryKey = ["accounts", restoredPayload, linkedTenantId]
  return useQuery({
    queryKey,
    queryFn: () => fetchAccounts(tokenInfo!, restoredPayload, linkedTenantId),
    enabled:
      !!tokenInfo && (!!location.pathname.includes("accounts") || enabled)
  })
}

export const useGetLast5EmailMessages = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["recent-email-templates"],
    queryFn: async () => {
      const res = await getLast5EmailTemplates(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export const useGetSavedTemplate = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["saved-template"],
    queryFn: async () => {
      const res = await getSavedTemplate(tokenInfo as ITokenInfo)
      return res.data?.data?.details[0] || ""
    },
    enabled: !!tokenInfo
  })
}

export const useGetOrbitshiftLocations = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["orbitshift-locations"],
    queryFn: async () => {
      const res = await getOrbitshiftLocations(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo
  })
}

export const useGetTenantUsersBasicDetails = (enabled: boolean) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["tenant-users-basic-details"],
    queryFn: async () => {
      const res = await getTenantUsersBasicDetails(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: enabled && !!tokenInfo
  })
}

export const useGetCollaborators = (searchText: string = "") => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["collaborators"],
    queryFn: async () => {
      const res = await getCollaborators(tokenInfo as ITokenInfo, searchText)
      return res.data
    }
  })
}

export const useGetAccountFunctionSegments = (accountId: any) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["acount-function-segments", accountId],
    queryFn: async () => {
      const res = await getAccountFunctionSegments(
        tokenInfo as ITokenInfo,
        accountId
      )
      return res.data
    },
    enabled: !!tokenInfo
  })
}
export const useGetAccountOfferings = (accountId: any) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["account-offerings", accountId],
    queryFn: async () => {
      const res = await getAccountOfferings(tokenInfo as ITokenInfo, accountId)
      return res.data
    },
    enabled: !!tokenInfo
  })
}
